import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowUpDown, ChevronRight, Check, Eye } from 'lucide-react';
import { Card } from '../ui/Card';
import { MasteryBadge } from '../shared/MasteryBadge';
import { AppState, ModuleId } from '../../types';
import { GroupId } from '../../config/systemConfig';
import { MODULES } from '../../data/modules';
import { Button } from '../ui/Button';
interface StudentData {
  id: string;
  name: string;
  data: AppState;
}
interface StudentTableProps {
  students: StudentData[];
  selectedGroup: GroupId;
  getModuleMastery: (learnerId: string, moduleId: ModuleId) => number;
  selectedStudentId?: string | null;
  onSelectStudent?: (studentId: string) => void;
  onViewDetails?: (studentId: string) => void;
}
type SortField = 'name' | 'level' | 'mastery' | 'lastActive';
type SortDirection = 'asc' | 'desc';
export function StudentTable({
  students,
  selectedGroup,
  getModuleMastery,
  selectedStudentId,
  onSelectStudent,
  onViewDetails
}: StudentTableProps) {
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  // Calculate overall mastery for each student
  const studentsWithMastery = useMemo(() => {
    return students.map(student => {
      const totalMastery = MODULES.reduce((sum, module) => sum + getModuleMastery(student.id, module.id), 0);
      const overallMastery = Math.round(totalMastery / MODULES.length);
      return {
        ...student,
        overallMastery,
        level: student.data.stats.level,
        xp: student.data.stats.xp
      };
    });
  }, [students, getModuleMastery]);
  // Sort students
  const sortedStudents = useMemo(() => {
    const sorted = [...studentsWithMastery].sort((a, b) => {
      let comparison = 0;
      switch (sortField) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'level':
          comparison = a.level - b.level;
          break;
        case 'mastery':
          comparison = a.overallMastery - b.overallMastery;
          break;
        case 'lastActive':
          // For now, sort by XP as proxy for activity
          comparison = a.xp - b.xp;
          break;
      }
      return sortDirection === 'asc' ? comparison : -comparison;
    });
    return sorted;
  }, [studentsWithMastery, sortField, sortDirection]);
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };
  const SortButton = ({
    field,
    label
  }: {
    field: SortField;
    label: string;
  }) => <button onClick={() => handleSort(field)} className="flex items-center gap-1 hover:text-indigo-600 transition-colors" data-id="element-5434">
      {label}
      <ArrowUpDown className={`w-4 h-4 ${sortField === field ? 'text-indigo-600' : 'text-slate-400'}`} data-id="element-5435" />
    </button>;
  if (students.length === 0) {
    return null;
  }
  return <Card className="overflow-hidden" data-id="element-5436">
      {/* Desktop Table */}
      <div className="hidden md:block overflow-x-auto" data-id="element-5437">
        <table className="w-full" data-id="element-5438">
          <thead className="bg-slate-50 border-b border-slate-200" data-id="element-5439">
            <tr data-id="element-5440">
              <th className="text-left p-4 text-sm font-bold text-slate-700" data-id="element-5441">
                <SortButton field="name" label="Naam" data-id="element-5442" />
              </th>
              <th className="text-left p-4 text-sm font-bold text-slate-700" data-id="element-5443">
                <SortButton field="level" label="Level" data-id="element-5444" />
              </th>
              <th className="text-left p-4 text-sm font-bold text-slate-700" data-id="element-5445">
                <SortButton field="mastery" label="Beheersing" data-id="element-5446" />
              </th>
              <th className="text-left p-4 text-sm font-bold text-slate-700" data-id="element-5447">
                XP
              </th>
              <th className="text-right p-4 text-sm font-bold text-slate-700" data-id="element-5448">
                Acties
              </th>
            </tr>
          </thead>
          <tbody data-id="element-5449">
            {sortedStudents.map((student, index) => {
            const isSelected = selectedStudentId === student.id;
            return <motion.tr key={student.id} initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: index * 0.05
            }} className={`
                    border-b border-slate-100 last:border-0 transition-colors
                    ${isSelected ? 'bg-emerald-50/50' : 'hover:bg-slate-50'}
                  `} data-id="element-5450">
                  <td className="p-4" data-id="element-5451">
                    <div className="flex items-center gap-3" data-id="element-5452">
                      <div className={`
                        w-10 h-10 rounded-full flex items-center justify-center text-white font-bold transition-colors
                        ${isSelected ? 'bg-emerald-500' : 'bg-gradient-to-br from-indigo-400 to-indigo-600'}
                      `} data-id="element-5453">
                        {student.name.charAt(0).toUpperCase()}
                      </div>
                      <div data-id="element-5454">
                        <span className={`font-medium ${isSelected ? 'text-emerald-900' : 'text-slate-900'}`} data-id="element-5455">
                          {student.name}
                        </span>
                        {isSelected && <div className="text-xs text-emerald-600 font-bold flex items-center gap-1" data-id="element-5456">
                            <Check className="w-3 h-3" data-id="element-5457" /> Geselecteerd
                          </div>}
                      </div>
                    </div>
                  </td>
                  <td className="p-4" data-id="element-5458">
                    <div className="flex items-center gap-2" data-id="element-5459">
                      <div className="px-2 py-1 bg-amber-100 text-amber-700 rounded-lg text-sm font-bold" data-id="element-5460">
                        {student.level}
                      </div>
                    </div>
                  </td>
                  <td className="p-4" data-id="element-5461">
                    <MasteryBadge percentage={student.overallMastery} size="md" data-id="element-5462" />
                  </td>
                  <td className="p-4" data-id="element-5463">
                    <span className="text-sm text-slate-600 font-medium" data-id="element-5464">
                      {student.xp}
                    </span>
                  </td>
                  <td className="p-4 text-right" data-id="element-5465">
                    <div className="flex items-center justify-end gap-2" data-id="element-5466">
                      {onSelectStudent && <Button onClick={() => onSelectStudent(student.id)} variant={isSelected ? 'secondary' : 'ghost'} size="sm" className={isSelected ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200' : ''} data-id="element-5467">
                          {isSelected ? 'Geselecteerd' : 'Selecteer'}
                        </Button>}

                      {onViewDetails && <Button onClick={() => onViewDetails(student.id)} variant="ghost" size="sm" className="text-slate-500" data-id="element-5468">
                          <Eye className="w-4 h-4" data-id="element-5469" />
                        </Button>}
                    </div>
                  </td>
                </motion.tr>;
          })}
          </tbody>
        </table>
      </div>

      {/* Mobile Cards */}
      <div className="md:hidden divide-y divide-slate-100" data-id="element-5470">
        {sortedStudents.map((student, index) => {
        const isSelected = selectedStudentId === student.id;
        return <motion.div key={student.id} initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: index * 0.05
        }} className={`p-4 ${isSelected ? 'bg-emerald-50/30' : ''}`} data-id="element-5471">
              <div className="flex items-center justify-between mb-3" data-id="element-5472">
                <div className="flex items-center gap-3" data-id="element-5473">
                  <div className={`
                    w-12 h-12 rounded-full flex items-center justify-center text-white font-bold
                    ${isSelected ? 'bg-emerald-500' : 'bg-gradient-to-br from-indigo-400 to-indigo-600'}
                  `} data-id="element-5474">
                    {student.name.charAt(0).toUpperCase()}
                  </div>
                  <div data-id="element-5475">
                    <div className="font-bold text-slate-900 flex items-center gap-2" data-id="element-5476">
                      {student.name}
                      {isSelected && <Check className="w-4 h-4 text-emerald-500" data-id="element-5477" />}
                    </div>
                    <div className="text-sm text-slate-500" data-id="element-5478">
                      Level {student.level} • {student.xp} XP
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between mb-4" data-id="element-5479">
                <span className="text-sm text-slate-600" data-id="element-5480">Beheersing</span>
                <MasteryBadge percentage={student.overallMastery} size="md" data-id="element-5481" />
              </div>

              <div className="grid grid-cols-2 gap-2" data-id="element-5482">
                {onSelectStudent && <Button onClick={() => onSelectStudent(student.id)} variant={isSelected ? 'secondary' : 'primary'} size="sm" className={isSelected ? 'bg-emerald-100 text-emerald-700' : ''} fullWidth data-id="element-5483">
                    {isSelected ? 'Geselecteerd' : 'Selecteer'}
                  </Button>}
                {onViewDetails && <Button onClick={() => onViewDetails(student.id)} variant="outline" size="sm" fullWidth data-id="element-5484">
                    Details
                  </Button>}
              </div>
            </motion.div>;
      })}
      </div>
    </Card>;
}